import { useAppForm } from '@/components/form/formHooks';
import { useCreateTerminKalender } from '@/domain/termine/hooks/useCreateTerminKalender';
import {
  MarkierungType,
  markierungTypes,
} from '@/domain/termine/shared/types/Markierung';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useToast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { type TFunction } from 'i18next';
import z from 'zod';

type FormValues = {
  titel: string;
  markierung: MarkierungType;
};

const createValidationSchema = (t: TFunction) =>
  z.object({
    titel: z.string().min(1, t('domain.termine.sheet.validation.titel')),
    markierung: z.enum(markierungTypes),
  });

export function useCreateTerminKalenderForm() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { createTerminKalender } = useCreateTerminKalender();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();

  const defaultValues: FormValues = {
    titel: '',
    markierung: markierungTypes[19],
  };

  return useAppForm({
    defaultValues,
    validators: {
      onBlur: createValidationSchema(t),
    },
    onSubmit: async ({ value }) => {
      try {
        const response = await createTerminKalender({
          name: value.titel,
          markierung: value.markierung,
          standortId,
        });

        if (!response) {
          throw Error('No response from createTerminKalender mutation');
        }

        if (response?.error) {
          toast({
            title: t('domain.termine.kalender.errorCreatedTerminCalendar'),
            description: response.error.message,
          });
          return;
        }

        toast({
          title: t('domain.termine.kalender.createdTerminCalendar', {
            name: value.titel,
          }),
        });
      } catch (error) {
        Sentry.captureException(error);
        toast({
          title: t('domain.termine.kalender.errorCreatedTerminCalendar'),
        });
        throw Error(error instanceof Error ? error.message : String(error));
      }
    },
  });
}
